import React from 'react';
import { CheckCircle2, Shield, Award, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';

export const ServiceTemplate = ({ service, onOpenPopup, showPricing = false, PricingComponent }) => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-[#58225a] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGF0dG9ybmV5JTIwY29uc3VsdGF0aW9uJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYyODQ4ODB8MA&ixlib=rb-4.1.0&q=85" alt="Business people signing a contract" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{service.title}</h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">{service.description}</p>
            <Button size="lg" onClick={onOpenPopup} className="bg-[#ba2a84] text-white hover:bg-[#8a1f63] text-lg px-8 py-6 font-semibold">
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">Trademark Registration Services Designed for Growing Brands</h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                {service.overview.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="mt-8">
                <Button onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white px-8 py-4 text-lg font-semibold">
                  Start Your Trademark Application
                </Button>
              </div>
            </div>
            <div className="w-full">
              <img src="https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50cyUyMGNvbnRyYWN0JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzYyODQ4OTZ8MA&ixlib=rb-4.1.0&q=85" alt="Man signing a document" className="w-full h-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Why Choose Trademark Examining Lawyer?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive trademark services delivered by professionals focused on brand protection.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, t: "Complete Brand Protection", d: "We manage the entire process, from preliminary trademark searches to post-registration support." },
              { icon: Award, t: "Experienced Trademark Professionals", d: "Our team focuses exclusively on trademark and intellectual property matters." },
              { icon: Clock, t: "Fast & Efficient Filing", d: "Streamlined procedures help reduce delays and ensure timely application submission." },
              { icon: Users, t: "Personalized Assistance", d: "A dedicated support specialist remains available throughout every phase of your case." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-4"><c.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Shield, t: "Complete Brand Protection", d: "We manage the entire process, from preliminary trademark searches to post-registration support." },
              { icon: Award, t: "Experienced Trademark Professionals", d: "Our team focuses exclusively on trademark and intellectual property matters." },
              { icon: Clock, t: "Fast & Efficient Filing", d: "Streamlined procedures help reduce delays and ensure timely application submission." },
              { icon: Users, t: "Personalized Assistance", d: "A dedicated support specialist remains available throughout every phase of your case." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-4"><c.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Our Registration Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">A simple three-step approach designed to make trademark protection straightforward.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { n: "1", t: "Initial Consultation", d: "Speak with our specialists about your business, trademark goals, and the most effective registration strategy." },
              { n: "2", t: "Research & Preparation", d: "We conduct trademark searches, prepare required documentation, and ensure proper trademark classification." },
              { n: "3", t: "Filing & Protection", d: "Your application is submitted to the USPTO and actively monitored through every stage until a final decision is reached." }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-all relative">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#58225a] text-white mb-6"><span className="text-3xl font-bold">{s.n}</span></div>
                <h3 className="text-2xl font-bold text-[#58225a] mb-4">{s.t}</h3>
                <p className="text-gray-600 leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel autoPlayInterval={4500}>
            {[
              { n: "1", t: "Initial Consultation", d: "Speak with our specialists about your business, trademark goals, and the most effective registration strategy." },
              { n: "2", t: "Research & Preparation", d: "We conduct trademark searches, prepare required documentation, and ensure proper trademark classification." },
              { n: "3", t: "Filing & Protection", d: "Your application is submitted to the USPTO and actively monitored through every stage until a final decision is reached." }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#58225a] text-white mb-6"><span className="text-3xl font-bold">{s.n}</span></div>
                <h3 className="text-2xl font-bold text-[#58225a] mb-4">{s.t}</h3>
                <p className="text-gray-600 leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </MobileCarousel>
          <div className="text-center mt-12">
            <Button onClick={onOpenPopup} size="lg" className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white px-10 py-6 text-lg font-semibold">
              Begin Your Registration Today
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Advantages of Federal Trademark Registration</h2>
            <p className="text-lg text-gray-600">Discover why trademark protection is an important investment in your business future.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.benefits.map((b, i) => (
              <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0"><CheckCircle2 className="h-6 w-6 text-[#e8a8c8]" /></div>
                  <div>
                    <h3 className="font-bold text-[#58225a] mb-2">{b.title}</h3>
                    <p className="text-gray-600">{b.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {service.benefits.map((b, i) => (
              <Card key={i} className="p-6 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0"><CheckCircle2 className="h-6 w-6 text-[#e8a8c8]" /></div>
                  <div>
                    <h3 className="font-bold text-[#58225a] mb-2">{b.title}</h3>
                    <p className="text-gray-600">{b.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {showPricing && PricingComponent && <PricingComponent onOpenPopup={onOpenPopup} />}

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            <div className="order-2 lg:order-1 w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600 mb-8">Answers to common concerns about trademark registration services.</p>
              <Accordion type="single" collapsible className="space-y-4">
                {service.faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-gray-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-[#58225a] hover:text-[#e8a8c8]">
                      <div className="flex items-center space-x-3">
                        <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                        <span>{faq.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pl-11 pt-2">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="mt-8">
                <Button onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white px-8 py-6 text-lg font-semibold">
                  Start Your Trademark Application
                </Button>
              </div>
            </div>
            <div className="order-1 lg:order-2 w-full">
              <img src="https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGF0dG9ybmV5JTIwY29uc3VsdGF0aW9uJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYyODQ4ODB8MA&ixlib=rb-4.1.0&q=85" alt="Group sitting around a table discussing" className="w-full h-full object-cover rounded-2xl shadow-2xl min-h-[600px]" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1768380866961-286e2b0a89b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City buildings illuminated at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Protect Your Brand With Confidence</h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
            Take the next step toward securing your intellectual property with trusted trademark registration services.
          </p>
          <Button size="lg" onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white text-lg px-8 py-6 font-semibold">
            Start Your Trademark Application
          </Button>
        </div>
      </section>
    </div>
  );
};
